import useShop from "@babackapp/private-api-client/client/use-shop";
import useTranslation from "@babackapp/translations/use-translation";
import { LoadingButton } from "@babackapp/ui/components/loading-button";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@babackapp/ui/components/sidebar";
import useIsMobile from "@babackapp/ui/lib/use-is-mobile";
import { ArrowRight } from "lucide-react";
import { useEffect } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import logoWhiteOther from "@/assets/logoWithName-colors.svg";
import BlockNotPaying from "@/components/block-not-paying.tsx";
import ShopifyDisconnected from "@/components-legacy/shopify-disconnected.tsx";
import config from "@/config.ts";
import { useLDFlags } from "@/hooks/use-ld-flags.ts";
import { FrontChat } from "@/providers/frontchat/frontchat.provider.tsx";
import ShopifyScopeBanner from "./components/shopifyScopeBanner";
import { AppSidebar } from "./components/sidebar/sidebar";
import TrackingBanner from "@/components/tracking-banner";

export default function ShopLayout() {
  return <Content />;
}

function Content() {
  const shopId = useParams<{ shopId: string }>().shopId;
  const { data: shop, isLoading, isError } = useShop(shopId);

  const navigate = useNavigate();
  const { hasAccessToNewBo, flagLoading } = useLDFlags({
    shopId: shop?.id || shopId || "",
  });
  const redirectToMeteorApp = (shopId: string) => {
    window.location.href = `${config.meteor.url}/shops/${shopId}`;
  };

  useEffect(() => {
    const canAccessToNewBo = !hasAccessToNewBo && !flagLoading;
    if (canAccessToNewBo && process.env.NODE_ENV === "production")
      redirectToMeteorApp(shopId || "");
  }, [hasAccessToNewBo, flagLoading]);

  useEffect(() => {
    if (shop?.name) {
      document.title = `Baback - ${shop.name}`;
    }
  }, [shop?.name]);

  if (isLoading || flagLoading) return <Fallback />;
  if (isError) navigate("/");
  if (shop?.shopifyStatus != "active")
    return <ShopifyDisconnected shopifyUrl={shop?.shopifyUrl || ""} />;
  if (shop?.billing.stripe.blockNotPaying) return <BlockNotPaying />;

  const scopes = [
    "read_script_tags",
    "write_script_tags",
    "write_order_edits",
    "write_orders",
    "read_order_edits",
    "write_draft_orders",
    "read_draft_orders",
    "write_gift_cards",
    "read_gift_cards",
    "read_orders",
    "read_products",
    "read_customers",
    "read_themes",
    "write_discounts",
    "read_discounts",
    "read_price_rules",
    "write_price_rules",
    "read_inventory",
    "write_inventory",
    "read_shipping",
    "write_shipping",
    "write_returns",
    "read_translations",
    "unauthenticated_write_checkouts",
    "read_checkouts",
    "write_checkouts",
    "read_assigned_fulfillment_orders",
    "write_assigned_fulfillment_orders",
    "read_merchant_managed_fulfillment_orders",
    "write_merchant_managed_fulfillment_orders",
    "read_third_party_fulfillment_orders",
    "write_third_party_fulfillment_orders",
    "read_fulfillments",
    "write_fulfillments",
    "read_locations",
    "write_locations",
    "write_customers",
    "read_all_orders",
  ];
  const hasToReconnect = !scopes.every((scope) =>
    shop.shopifyScopes.split(",").includes(scope),
  );
  const isShopifyTestWebsite = shop.email.includes("shopify.com");
  return (
    <SidebarProvider>
      {hasToReconnect && <ShopifyScopeBanner shopifyUrl={shop.shopifyUrl} />}
      {!isShopifyTestWebsite && <TrackingBanner />}
      <AppSidebar />
      <SidebarInset className="flex-1 overflow-x-auto">
        <div className="pb-32 h-full min-w-0">
          <MobileSidebarTrigger />
          <Outlet />
          {config.isProd && <FrontChat />}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

function MobileSidebarTrigger() {
  const isMobile = useIsMobile();
  if (isMobile) return <SidebarTrigger />;
}

function Fallback() {
  const t = useTranslation();

  function redirectToBaback() {
    window.location.href = import.meta.env.VITE_URL;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="flex flex-col items-center justify-center space-y-4">
        <img className="h-20 w-auto" src={logoWhiteOther} alt="Logo Baback" />

        <div className="text-center pl-10">
          <div className="flex items-center space-x-1 justify-center">
            <h3 className="text-primary text-xl">Loading</h3>
            <LoadingButton isLoading={true} variant="link" />
          </div>
          <p className="text-gray-600 ">
            {t("home.loadingToLong")}
            <span
              className="font-semibold ml-1 hover:cursor-pointer inline-flex items-center border-b border-gray-600"
              onClick={redirectToBaback}
            >
              {t("misc.thisLink")}
              <ArrowRight className="h-4 w-4 ml-1.5 inline" />
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
